import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CheckCircle2, Ban, FileText, BarChart3, Search, Download, Eye, ChevronLeft, ChevronRight, Home } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { EmptyState } from "@/components/EmptyState";
import { useCompany } from "@/context/CompanyContext";

/* Companies page layout (the legal entities). Branch offices under a company are on the Branches page. Presentation only: Companies.jsx still owns loading, toggling, the create/delete dialogs and the drawer. */
const TEAL = "#0F7C8C";
const PAGE = 10;

const TONES = {
  blue:   { card: "from-[#EEF4FE] to-white border-[#DCE7F8] dark:from-[#1B2740] dark:to-[#202733] dark:border-[#2B3A57]", tile: "bg-[#DDE9FB] text-[#2563EB] dark:bg-[#243556] dark:text-[#8FB6F0]", bars: "text-[#2563EB] dark:text-[#8FB6F0]" },
  green:  { card: "from-[#EAF8F0] to-white border-[#D3EFDF] dark:from-[#17302A] dark:to-[#202733] dark:border-[#24463B]", tile: "bg-[#D5F1E2] text-[#16A34A] dark:bg-[#1F4034] dark:text-[#6FD1AA]", bars: "text-[#16A34A] dark:text-[#6FD1AA]" },
  red:    { card: "from-[#FDECEF] to-white border-[#F8D5DB] dark:from-[#3A1F29] dark:to-[#202733] dark:border-[#5A2C3A]", tile: "bg-[#FAD9DF] text-[#E11D48] dark:bg-[#4E2634] dark:text-[#F0899F]", bars: "text-[#E11D48] dark:text-[#F0899F]" },
  orange: { card: "from-[#FEF4E6] to-white border-[#F9E3C4] dark:from-[#3A3018] dark:to-[#202733] dark:border-[#554620]", tile: "bg-[#FCE6C7] text-[#F59E0B] dark:bg-[#4A3D1C] dark:text-[#E8BE5A]", bars: "text-[#F59E0B] dark:text-[#E8BE5A]" },
};

function StatTile({ tone, icon: Icon, label, value, loading }) {
  const t = TONES[tone];
  return (
    <div className={`flex items-center gap-4 rounded-2xl border bg-gradient-to-br p-4 shadow-sm ${t.card}`} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl ${t.tile}`}><Icon className="h-7 w-7" /></span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-3xl font-bold tabular-nums text-foreground">{loading ? "–" : value}</p>
      </div>
      <BarChart3 className={`h-6 w-6 shrink-0 self-end ${t.bars}`} aria-hidden />
    </div>
  );
}

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const heads = Object.keys(rows[0]);
  const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [heads.join(","), ...rows.map((r) => heads.map((h) => esc(r[h])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function CompaniesDesign({ companies, shown, loading, search, onSearch, isOrgAdmin, onToggle, onView, createAction, renderDelete }) {
  const { singleCompany } = useCompany() || {};
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search]);
  const pages = Math.max(1, Math.ceil(shown.length / PAGE));
  const cur = Math.min(page, pages);
  const rows = useMemo(() => shown.slice((cur - 1) * PAGE, cur * PAGE), [shown, cur]);
  const iconBtn = "grid h-9 w-9 place-items-center rounded-lg bg-secondary text-muted-foreground transition-colors hover:bg-accent hover:text-foreground";

  return (
    <div className="space-y-6" data-testid="companies-page">
      {/* header */}
      <div>
        <nav className="mb-3 flex items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb">
          <Link to="/" className="inline-flex items-center gap-1.5 hover:text-foreground"><Home className="h-3.5 w-3.5" />Home</Link>
          <ChevronRight className="h-3.5 w-3.5" /><span>Operations</span><ChevronRight className="h-3.5 w-3.5" /><span className="font-medium text-foreground">{singleCompany ? "Company Profile" : "Companies"}</span>
        </nav>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#E4EEF9] text-[#0F5C7A] dark:bg-[#243556] dark:text-[#8FB6F0]"><Building2 className="h-7 w-7" /></span>
            <div className="min-w-0">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">{singleCompany ? "Company Profile" : "Companies"}</h1>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{singleCompany ? "Your company: the legal entity whose books you keep. Its name, GSTIN and state appear on your documents. Offices and sites are on the Branches page." : "Your companies / legal entities (e.g. SA Construction, SARPL). Projects, accounts and documents are kept per company. Branch offices and sites under a company are on the Branches page."}</p>
            </div>
          </div>
          {createAction && <div className="shrink-0">{createAction}</div>}
        </div>
      </div>

      {/* stat tiles */}
      {!singleCompany && <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="page-stat-cards">
        <StatTile tone="blue" icon={Building2} label="Companies" value={companies.length} loading={loading} />
        <StatTile tone="green" icon={CheckCircle2} label="Active" value={companies.filter((c) => c.is_active).length} loading={loading} />
        <StatTile tone="red" icon={Ban} label="Inactive" value={companies.filter((c) => !c.is_active).length} loading={loading} />
        <StatTile tone="orange" icon={FileText} label="With GSTIN" value={companies.filter((c) => c.gstin).length} loading={loading} />
      </div>}

      {/* table card */}
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="table-toolbar">
          <div className="relative min-w-[220px] max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="toolbar-search-input" value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search..."
              className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#0F7C8C] focus:ring-2 focus:ring-[#0F7C8C]/20" />
          </div>
          {shown.length > 0 && (
            <button type="button" data-testid="toolbar-export-button" className="inline-flex h-11 items-center gap-2 rounded-lg border border-border bg-background px-4 text-sm font-semibold text-foreground shadow-sm hover:bg-accent"
              onClick={() => exportCsv(shown.map((c) => ({ Code: c.code, Name: c.name, GSTIN: c.gstin || "", State: c.state || "", Status: c.is_active ? "Active" : "Inactive" })), "companies.csv")}>
              <Download className="h-4 w-4" />Export
            </button>
          )}
        </div>

        {!loading && companies.length === 0 ? (
          <EmptyState title="No companies yet" description="Create your first company to get started." testId="companies-empty-state" />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60 hover:bg-secondary/60">
                  {["Code", "Name", "GSTIN", "State", "Status"].map((h) => <TableHead key={h} className="h-12 text-xs font-bold uppercase tracking-wide text-muted-foreground">{h}</TableHead>)}
                  <TableHead className="h-12 text-right text-xs font-bold uppercase tracking-wide text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} data-testid={`company-row-${c.code}`} className="h-[68px]">
                    <TableCell className="text-sm font-bold text-foreground">{c.code}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-3 text-sm font-semibold text-foreground">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-secondary text-muted-foreground"><Building2 className="h-4 w-4" /></span>{c.name}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{c.gstin || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.state || "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Switch checked={c.is_active} disabled={!isOrgAdmin} onCheckedChange={() => onToggle(c)} className="h-6 w-11 data-[state=checked]:bg-[#0F7C8C]" data-testid={`company-active-toggle-${c.code}`} />
                        <span className={`rounded-md px-2.5 py-1 text-xs font-semibold ${c.is_active ? "bg-[#D5EEF1] text-[#0B6573] dark:bg-[#17383D] dark:text-[#6CCBD6]" : "bg-secondary text-muted-foreground"}`}>{c.is_active ? "Active" : "Inactive"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2" data-testid={`company-actions-${c.code}`}>
                        <button type="button" title="View" onClick={() => onView(c)} data-testid={`company-actions-${c.code}-view`} className={iconBtn}><Eye className="h-4 w-4" /></button>
                        {renderDelete?.(c)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No companies match your search.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">Showing {rows.length} of {shown.length} {shown.length === 1 ? "company" : "companies"}</p>
          <div className="flex items-center gap-2">
            <button type="button" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label="Previous page" className="grid h-10 w-10 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" onClick={() => setPage(n)} aria-current={n === cur ? "page" : undefined}
                className={`grid h-10 w-10 place-items-center rounded-lg border text-sm font-semibold ${n === cur ? "border-[#0F7C8C] bg-[#E1F2F4] text-[#0B6573] dark:bg-[#17383D] dark:text-[#6CCBD6]" : "border-border text-muted-foreground hover:bg-accent"}`}>{n}</button>
            ))}
            <button type="button" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label="Next page" className="grid h-10 w-10 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
